import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ProformaLong} from '../../models/proforma-long-response.model';
import {
    DEFAULT_PAGINATION_META,
    loadPaginationPrefs,
    PaginatedState,
    PaginationParams,
    savePaginationPrefs,
} from '../../models/pagination.model';
import {
    getProformasForEmployee,
    loadPendingProformas,
    loadProformasForCurrentCompany,
} from '../../services/proforma.servcice';

interface ProformasState extends PaginatedState<ProformaLong> {
    context: 'pending' | 'company' | 'employee';
    employeeId?: number;
    projectId?: number;
}

const prefs = loadPaginationPrefs('proformas');

const initialState: ProformasState = {
    items: [],
    pagination: {...DEFAULT_PAGINATION_META, pageSize: prefs.pageSize},
    sort: {sortBy: prefs.sortBy || 'id', sortDirection: prefs.sortDirection},
    loading: false,
    error: null,
    stale: false,
    context: 'pending',
};

export const fetchProformas = createAsyncThunk(
    'proformas/fetchPage',
    async (args: { context: 'pending' | 'company' | 'employee'; employeeId?: number; projectId?: number; params: PaginationParams }) => {
        switch (args.context) {
            case 'pending':
                return await loadPendingProformas(args.params);
            case 'company':
                return await loadProformasForCurrentCompany(args.params);
            case 'employee':
                return await getProformasForEmployee(args.employeeId!, args.projectId, args.params);
        }
    }
);

const proformasSlice = createSlice({
    name: 'proformas',
    initialState,
    reducers: {
        setContext(state, action: PayloadAction<{ context: 'pending' | 'company' | 'employee'; employeeId?: number; projectId?: number }>) {
            state.context = action.payload.context;
            state.employeeId = action.payload.employeeId;
            state.projectId = action.payload.projectId;
            state.pagination.pageNumber = 0;
            state.stale = true;
        },
        setPage(state, action: PayloadAction<number>) {
            state.pagination.pageNumber = action.payload;
            state.stale = true;
        },
        setPageSize(state, action: PayloadAction<number>) {
            state.pagination.pageSize = action.payload;
            state.pagination.pageNumber = 0;
            state.stale = true;
            savePaginationPrefs('proformas', {pageSize: action.payload, sortBy: state.sort.sortBy, sortDirection: state.sort.sortDirection});
        },
        setSort(state, action: PayloadAction<{ sortBy: string; sortDirection: 'ASC' | 'DESC' }>) {
            state.sort = action.payload;
            state.pagination.pageNumber = 0;
            state.stale = true;
            savePaginationPrefs('proformas', {pageSize: state.pagination.pageSize, ...action.payload});
        },
        markStale(state) {
            state.stale = true;
        },
        updateItem(state, action: PayloadAction<ProformaLong>) {
            const index = state.items.findIndex(item => item.id === action.payload.id);
            if (index !== -1) {
                state.items[index] = action.payload;
            }
        },
        removeItem(state, action: PayloadAction<number>) {
            state.items = state.items.filter(item => item.id !== action.payload);
            state.pagination.totalRecords = Math.max(0, state.pagination.totalRecords - 1);
            state.pagination.totalPageItems = state.items.length;
            state.pagination.totalPages = Math.ceil(state.pagination.totalRecords / state.pagination.pageSize);
            if (state.items.length === 0 && state.pagination.pageNumber > 0) {
                state.pagination.pageNumber -= 1;
                state.stale = true;
            }
        },
        resetState() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProformas.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProformas.fulfilled, (state, action) => {
                const response = action.payload;
                state.items = response.body || [];
                if (response.pagination) {
                    state.pagination = response.pagination;
                }
                state.loading = false;
                state.stale = false;
            })
            .addCase(fetchProformas.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch proformas';
            });
    },
});

export const proformasActions = {...proformasSlice.actions, fetchProformas};
export const proformasReducer = proformasSlice.reducer;
